import { ErrorPanel, InfoCard, Progress } from '@backstage/core-components';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { createFrontendPlugin } from '@backstage/frontend-plugin-api';
import { EntityCardBlueprint } from '@backstage/plugin-catalog-react/alpha';
import { useEntity } from '@backstage/plugin-catalog-react';
import { Link } from '@material-ui/core';
import { useEffect, useState } from 'react';

type Ssp = {
  service: string;
  status: string;
  lastModified: string;
  reviewDue: string;
  url: string;
};

function SspStatusCard() {
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const service = entity.metadata.name;
  const [ssp, setSsp] = useState<Ssp | undefined>(undefined);
  const [error, setError] = useState<Error>();
  const componentName = entity.metadata.title ?? entity.metadata.name;

  useEffect(() => {
    if (!service) {
      return;
    }

    let cancelled = false;

    async function loadSsp() {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('ssp');
        const response = await fetchApi.fetch(
          `${baseUrl}/${encodeURIComponent(service)}`,
        );

        if (!response.ok) {
          throw new Error(
            `SSP returned ${response.status} while loading status.`,
          );
        }

        if (!cancelled) {
          setSsp((await response.json()) as Ssp);
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(
            loadError instanceof Error
              ? loadError
              : new Error('Could not load SSP information.'),
          );
        }
      }
    }
    loadSsp();

    return () => {
      cancelled = true;
    };
  }, [discoveryApi, fetchApi, service]);

  if (error) {
    return <ErrorPanel error={error} title="SSP information unavailable" />;
  }

  if (!ssp) {
    return <Progress />;
  }

  return (
    <InfoCard title={`${componentName} System Security Policy`}>   
        <p>
            <strong>Status:</strong> {ssp.status}
        </p>
    
        <p>
        <strong>Last Modified:</strong>
            {' '}
            {ssp.lastModified}
        </p>
        
        <p>
        <strong>Review Due:</strong>
            {' '}
            {ssp.reviewDue}
        </p>
        
        <Link
            href={ssp.url}
            target="_blank"
            rel="noopener noreferrer"
        >
            Open SSP
        </Link> 
    </InfoCard>
    );
}
    

const sspStatusCard = EntityCardBlueprint.make({
    name: 'ssp-status',
    params: {
        filter: entity => entity.kind === 'Component',
        loader: async () => <SspStatusCard />,
    },
});


export const sspPlugin = createFrontendPlugin({
    pluginId: 'ssp',
    extensions: [sspStatusCard],
});